import React from 'react'
import { observer } from 'mobx-react'
import { Button, message } from 'antd'
import { store } from '../../store'
import { t } from '../../config/i18n'
import { routes } from '../../config/routes'
import { CheckOutlined, SettingOutlined } from '@ant-design/icons'
import { ColumnsType } from 'antd/es/table'
import { TSingleCatalogItem } from '../../models/SingleCatalogItem'
import { TableActionsProps } from '../shared/ui/table/Actions'
import { toString } from '../../lib/utilsCommon'
import { useParams } from 'react-router'
import { hasAccess } from '../../lib/utilsCatalogs'
import { useCatalog } from '../../hooks/useCatalog'
import { useNavigate } from '../../hooks/useNavigate'
import { PageSettings } from '../shared/ui/PageSettings'
import { modalConfirm } from '../../lib/utilsUI'
import { PageTitleButton } from '../shared/ui/page/TitleButton'
import { IPaginationSettings, Table } from '../shared/ui/Table'
import { PERMISSIONS } from '../../models/AbstractCatalogConfig'
import { TAbstractCatalogItem } from '../../models/AbstractCatalogItem'
import { AbstractCatalogService, TCatalogType } from '../../services/AbstractCatalogService'
import { PageCatalogItemListFilter } from './pageCatalogItemList/Filter'
import { TableCellText } from '../shared/ui/table/CellText'

export const PageCatalogItemList = observer(() => {
  const navigate = useNavigate()
  const params = useParams()
  const { type } = params
  const parentId = parseInt(`${params.parentId}`) || 0
  const { catalogService, texts } = useCatalog(type, 'catalog_item_list')
  const itemLoadingId = catalogService.store.itemLoadingId

  const loadItems = React.useCallback((page?: number, pageSize?: number) => {
    params.id && catalogService.loadCatalogItems(params.id, parentId, page, pageSize).catch(window.logger.error)
  }, [params.id, parentId])

  React.useEffect(() => {
    catalogService.setItemListFilter({}, false).catch(window.logger.error)
    loadItems()
  }, [loadItems, params.id, parentId])

  const onDelete = async (item: TAbstractCatalogItem) => {
    const config = await catalogService.getCatalogConfig(item.catalogId)
    if (config && hasAccess(config, PERMISSIONS.ITEM_DELETE)) {
      modalConfirm({
        okText: t.common.delete,
        onOk: async () => {
          const catalogId = item.catalogId
          await catalogService.deleteCatalogItem(item)
          await catalogService.loadCatalogItems(catalogId)
        },
      })
    } else {
      message.error(t.common.accessDenied)
    }
  }

  const columns = getColumns(type as TCatalogType, navigate, catalogService)

  const getPagination = (): IPaginationSettings | null => {
    if (catalogService.store.total < catalogService.store.pageSize) { return null }
    return {
      page: catalogService.store.page,
      total: catalogService.store.total,
      pageSize: catalogService.store.pageSize,
      onChange: loadItems,
    }
  }

  const tableActions = (item: TAbstractCatalogItem): TableActionsProps => ({
    loading: itemLoadingId === item._id,
    onEdit: () => navigate(routes.catalogItemDetail, { type, id: catalogService.store.configItemDetails.id, itemId: item._id, parentId: item.parentId }),
    onDelete: item.active ? () => onDelete(item) : undefined,
    extra: !item.active ? [
      [t.common.restore, () => {
        catalogService.saveCatalogItem(item, true)
          .then(catalogService.reloadCatalogItems)
          .catch(window.logger.error)
      }],
    ] : [],
  })

  return (
    <PageSettings
      loading={store.ui.loading}
      title={catalogService.store.configItemDetails.name}
      actionText={t.catalog.addElButton}
      onAction={() => navigate(routes.catalogItemDetail, { type, id: catalogService.store.configItemDetails.id, itemId: 'new', parentId })}
      titleActions={<PageTitleButton icon={<SettingOutlined />} onCLick={() => navigate(routes.catalogConfig, { type, id: catalogService.store.configItemDetails.id })} />}
      extraActions={<PageCatalogItemListFilter catalogService={catalogService} />}
    >
      <Table
        idField={'_id'}
        items={catalogService.store.itemList}
        columns={columns}
        pagination={getPagination()}
        onSortRows={catalogService.sortItem}
        actions={tableActions}
        onRowDoubleClick={item => tableActions(item).onEdit?.()}
      />
    </PageSettings>
  )
})

function getColumns (
  type: TCatalogType,
  navigate: Function,
  catalogService: AbstractCatalogService<any, any>,
): ColumnsType<TAbstractCatalogItem> {
  const config = catalogService.store.configItemDetails
  const result: ColumnsType<TAbstractCatalogItem> = [
    {
      key: 'id',
      dataIndex: 'id',
      title: 'ID',
      fixed: 'left',
      width: '1%',
    },
    {
      key: 'name',
      dataIndex: 'name',
      title: t.common.name,
      width: '100%',
      render: (_, item) => (
        item.isCategory
          ? <Button type="link" className={'pl0 text-left'} onClick={() => navigate(routes.catalogItemList, { type, id: item.catalogId, parentId: item.id })}>
              <TableCellText minWidth={400} maxWidth={'calc(100vw - 720px)'}>{item.name}</TableCellText>
            </Button>
          : <TableCellText minWidth={400} maxWidth={'calc(100vw - 720px)'}>{item.name}</TableCellText>
      )
    }
  ]

  if (catalogService.store.itemListFilter.showInactive) {
    result.push({
      dataIndex: 'active',
      title: 'active',
      align: 'center',
      render: (_, item) => item.active && <CheckOutlined style={{ color: 'green' }} />
    })
  }

  if (type === 'single') {
    result.push({
      dataIndex: 'displayInWeb',
      title: 'displayInWeb',
      align: 'center',
      render: (_, item) => (item as TSingleCatalogItem).displayInWeb && <CheckOutlined style={{ color: 'green' }} />
    })
  }

  for (const fieldConfig of config.fields) {
    if (fieldConfig.displayInGrid) {
      result.push({
        key: fieldConfig.id,
        dataIndex: fieldConfig.id,
        title: fieldConfig.name,
        render: (_, item) => {
          const field = item.fields.find(i => i.fieldID === fieldConfig.id)
          return fieldConfig.fieldType === 'CHECKBOX'
            ? (field?.value && <CheckOutlined style={{ color: 'green' }} />)
            : toString(field?.resolvedValue || field?.value)
        },
      })
    }
  }

  return result
}
